import { useCallback, useEffect, useRef, useState } from "react";

import {
  SHEET_HEIGHT,
  cycleSheet,
  loadSheetPosition,
  saveSheetPosition,
  snapSheet,
  type SheetPosition,
} from "@/lib/split";

interface BottomSheetProps {
  /** Namespaces the persisted position, exactly as SplitPane namespaces the dock ratio. */
  surface: string;
  main: React.ReactNode;
  sheet: React.ReactNode;
  /** Rendered under the sheet and never covered by it — the tab bar. */
  footer?: React.ReactNode;
  /** The scrolling main region — the phone's half of `SplitPane`'s `mainRef`. See it for why. */
  mainRef?: React.Ref<HTMLDivElement>;
  /** Nudge the sheet open when something arrives in it (a lookup result, the radical picker). */
  openOn?: unknown;
}

/**
 * The phone form of the split (`6c`).
 *
 * The design: "The sheet is the dock. It rests at about 40% of the screen, drags to full height, and
 * swipes away to leave only text — the same grip, the same three positions as the desktop split."
 *
 * So this is not a modal. The log stays live and scrollable behind it, and dismissing the sheet is
 * how you get a clean page of Japanese to read — not how you cancel something.
 */
export function BottomSheet({ surface, main, sheet, footer, openOn, mainRef }: BottomSheetProps) {
  const [position, setPosition] = useState<SheetPosition>(() => loadSheetPosition(surface));
  /** Non-null only mid-drag; the sheet follows the finger, then snaps. */
  const [dragFraction, setDragFraction] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const move = useCallback(
    (next: SheetPosition) => {
      setPosition(next);
      saveSheetPosition(surface, next);
    },
    [surface],
  );

  // An answer arriving in a dismissed sheet would otherwise be invisible. Only ever opens it to
  // peek — never yanks it to full, and never closes one you deliberately opened.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (openOn !== undefined && openOn !== null) {
      setPosition((current) => (current === "hidden" ? "peek" : current));
    }
  }, [openOn]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      let moved = false;

      const onMove = (e: PointerEvent) => {
        moved = true;
        setDragFraction(Math.min(1, Math.max(0, (rect.bottom - e.clientY) / rect.height)));
      };
      const onUp = (e: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setDragFraction(null);
        // A tap cycles; a drag snaps to whichever position it ended nearest.
        move(moved ? snapSheet((rect.bottom - e.clientY) / rect.height) : cycleSheet(position));
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [move, position],
  );

  const fraction = dragFraction ?? SHEET_HEIGHT[position];

  return (
    // The footer is a SIBLING of this container, not a child: the sheet's height is a percentage,
    // and it has to resolve against the space above the tab bar. Nested, a full-height sheet put its
    // own grip underneath the bar and made the sheet impossible to grab.
    <>
      <div ref={containerRef} className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div ref={mainRef} className="min-h-0 flex-1 overflow-auto">
          {main}
        </div>

        <div
          // **Square-topped and sunken, which is what the Mobile canvas draws** — `#0d0d0c` under a
          // 1px `#3d3d3a` rule and no radius at all. It was `rounded-t-2xl` on `--panel`: a 16px
          // corner is four times the theme's largest radius, and a sheet the same ground as the
          // cards behind it needs the corner to read as a separate plane. Sunken does that
          // without one, and is the same relationship the desktop split already has.
          className="flex shrink-0 flex-col border-t border-line-strong bg-sunken shadow-[var(--sh-sheet)]"
          style={{
            height: `${fraction * 100}%`,
            // Swiped away still shows the grip — otherwise the sheet could never be brought back.
            minHeight: 26,
            // No animation mid-drag, or the sheet lags the finger.
            transition: dragFraction === null ? "height 160ms ease-out" : undefined,
          }}
        >
          <button
            type="button"
            onPointerDown={onPointerDown}
            aria-label={`Detail sheet — ${position}. Drag to resize, tap to cycle.`}
            data-check="sheet.grip"
            // A wide grab area around a 4px grip: the target has to be thumb-sized even though the
            // thing you see is a hairline.
            className="flex h-[26px] w-full shrink-0 cursor-ns-resize touch-none items-center justify-center pt-[10px]"
          >
            {/* 30×3, the canvas's own figures. */}
            <span className="h-[3px] w-[30px] rounded-[var(--r-2)] bg-line-strong" />
          </button>

          {/* Unmounted, not hidden, when swiped away — a dismissed sheet should cost nothing. */}
          {position !== "hidden" && (
            <div className="min-h-0 flex-1 overflow-auto px-4 pb-[14px]">{sheet}</div>
          )}
        </div>
      </div>

      {footer}
    </>
  );
}
